import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import NeuralCanvas from "@/components/NeuralCanvas";
import { SLOTS } from "@/data";
import { getTakenSlots, postBooking } from "@/lib/api";

function proximosDias(n = 10) {
  const dias = [];
  const nombres = ["DOM", "LUN", "MAR", "MIÉ", "JUE", "VIE", "SÁB"];
  const meses = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];
  const d = new Date();
  while (dias.length < n) {
    d.setDate(d.getDate() + 1);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) {
      const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      dias.push({ num: d.getDate(), dow: nombres[dow], mes: meses[d.getMonth()], iso });
    }
  }
  return dias;
}

export default function Agendar() {
  const navigate = useNavigate();
  const onBack = () => navigate("/");
  const [dias] = useState(() => proximosDias());
  const [dia, setDia] = useState(null);
  const [hora, setHora] = useState(null);
  const [taken, setTaken] = useState([]);
  const [f, setF] = useState({ nombre: "", email: "", tel: "" });
  const [confirmada, setConfirmada] = useState(false);
  const [loading, setLoading] = useState(false);
  const ok = f.nombre.trim().length > 1 && f.email.includes("@");
  const paso = confirmada ? 3 : dia && hora ? 2 : dia ? 1 : 0;

  useEffect(() => {
    if (!dia) return;
    setHora(null);
    getTakenSlots(dia.iso).then((r) => setTaken(r.taken || [])).catch(() => setTaken([]));
  }, [dia]);

  const confirmar = async () => {
    if (!ok || loading) return;
    setLoading(true);
    try {
      await postBooking({
        nombre: f.nombre, email: f.email, telefono: f.tel,
        dia: dia.iso, hora, dia_label: `${dia.dow} ${dia.num} ${dia.mes}`,
      });
      setConfirmada(true);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error("Esa franja acaba de reservarse. Elige otra hora.");
        getTakenSlots(dia.iso).then((r) => setTaken(r.taken || []));
        setHora(null);
      } else {
        toast.error("No se pudo agendar. Inténtalo de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (confirmada) {
    return (
      <div className="lci"><div className="agenda-wrap">
        <NeuralCanvas />
        <div className="co-ok rise" style={{ maxWidth: 560 }} data-testid="agenda-confirmada">
          <div className="big">📅</div>
          <h2>¡Llamada agendada!</h2>
          <p>
            Te esperamos el <b style={{ color: "var(--gold)" }}>{dia.dow} {dia.num} {dia.mes} a las {hora}</b> (hora Madrid).
            Te hemos enviado la invitación con el enlace de la videollamada a <b style={{ color: "var(--cream)" }}>{f.email}</b>.
            Es una llamada gratuita de 15 minutos para resolver todas tus dudas, sin ningún compromiso.
          </p>
          <button className="btn btn-gold" onClick={onBack}>Volver a la web →</button>
        </div>
      </div></div>
    );
  }

  return (
    <div className="lci"><div className="agenda-wrap">
      <NeuralCanvas />
      <div className="agenda">
        <button className="back" onClick={onBack}>← Volver</button>
        <div className="agenda-panel">
          <span className="sec-tag">Llamada gratuita · 15 min</span>
          <h2 className="serif" style={{ fontSize: 28, fontWeight: 600, margin: "14px 0 8px" }}>Agenda tu llamada con el equipo</h2>
          <p style={{ color: "var(--muted)", fontSize: 13.5, fontWeight: 300, lineHeight: 1.7, marginBottom: 26 }}>
            Resolvemos tus dudas sobre los cursos, el VIP vitalicio o tu caso concreto. Horarios en hora de Madrid.
          </p>
          <div className="agenda-steps">
            {[0, 1, 2].map((i) => <div key={i} className={`astep ${paso > i || (paso === i && i === 0) ? "on" : ""}`} />)}
          </div>

          <label style={{ fontSize: 11, letterSpacing: ".24em", textTransform: "uppercase", color: "var(--muted)" }}>1 · Elige el día</label>
          <div className="dias">
            {dias.map((d) => (
              <button key={d.iso} data-testid={`dia-${d.iso}`} className={`dia ${dia?.iso === d.iso ? "on" : ""}`} onClick={() => setDia(d)}>
                <span>{d.dow}</span><b>{d.num}</b><span>{d.mes}</span>
              </button>
            ))}
          </div>

          {dia && (
            <>
              <label style={{ fontSize: 11, letterSpacing: ".24em", textTransform: "uppercase", color: "var(--muted)" }}>2 · Elige la hora</label>
              <div className="slots">
                {SLOTS.map((s) => (
                  <button key={s} data-testid={`slot-${s}`} disabled={taken.includes(s)} className={`slot ${hora === s ? "on" : ""}`} onClick={() => setHora(s)}>{s}</button>
                ))}
              </div>
            </>
          )}

          {dia && hora && (
            <>
              <div className="agenda-resumen">
                <span>📅</span><span><b>{dia.dow} {dia.num} {dia.mes}</b> · <b>{hora}</b> (Madrid) · Videollamada de 15 min</span>
              </div>
              <div className="field-row">
                <div className="field">
                  <label htmlFor="ag-nombre">Nombre</label>
                  <input id="ag-nombre" data-testid="agenda-nombre" value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} placeholder="Tu nombre" />
                </div>
                <div className="field">
                  <label htmlFor="ag-tel">Teléfono / WhatsApp</label>
                  <input id="ag-tel" data-testid="agenda-tel" value={f.tel} onChange={(e) => setF({ ...f, tel: e.target.value })} placeholder="+34 · +52 · +54 …" />
                </div>
              </div>
              <div className="field">
                <label htmlFor="ag-email">Email</label>
                <input id="ag-email" data-testid="agenda-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="tu@email.com" />
              </div>
              <button className="btn btn-gold" data-testid="agenda-confirmar" style={{ width: "100%", justifyContent: "center", opacity: ok ? 1 : 0.45 }} onClick={confirmar}>
                {loading ? "Agendando…" : "Confirmar llamada →"}
              </button>
            </>
          )}
        </div>
      </div>
    </div></div>
  );
}
